<template>
    <div id="content" class="allergens" :class="[$mq]">
        <div v-if="showToolbar" class="toolbar">
            <div v-if="!isPortrait" class="allergens-tabs" role="tablist">
                <button
                    v-for="card in cards"
                    :key="card.id"
                    type="button"
                    role="tab"
                    class="allergens-tab"
                    :class="{ selected: activeTabId === card.id }"
                    :aria-selected="activeTabId === card.id"
                    @click="selectCard(card.id)"
                >
                    {{ card.name }}
                </button>
            </div>

            <div v-else class="allergens-select-wrap">
                <select
                    id="allergens-card-select"
                    class="allergens-select"
                    :value="activeTabId"
                    :aria-label="$t('page_title.Allergens')"
                    @change="onSelectChange"
                >
                    <option v-for="card in cards" :key="card.id" :value="card.id">
                        {{ card.name }}
                    </option>
                </select>
            </div>

            <button type="button" class="download-btn" :disabled="isDownloading || !activeCard" @click="onDownload">
                <i class="i-andy-download" aria-hidden="true"></i>
                <span>{{ $t('allergens.download') }}</span>
            </button>
        </div>

        <div class="allergens-content">
            <AllergensStatusMessage
                v-if="showStatusMessage"
                :title="statusTitle"
                :description="statusDescription"
            />
            <AllergensPdfViewer
                v-else-if="activeCard"
                :key="activeCard.id"
                :card-id="activeCard.id"
                :file-name="activeCard.fileName"
            />
        </div>
    </div>
</template>

<script>
import { Api } from '@/core/api.js'
import AllergensPdfViewer from '@/components/domain/allergens/AllergensPdfViewer'
import AllergensStatusMessage from '@/components/domain/allergens/AllergensStatusMessage'
import { getFilenameFromUrl, triggerBlobDownload } from '@/utils/allergensFiles'

const api = new Api()

export default {
    name: 'Allergens',
    components: {
        AllergensPdfViewer,
        AllergensStatusMessage
    },
    data() {
        return {
            cards: [],
            activeTabId: null,
            viewState: 'loading',
            isDownloading: false
        }
    },
    computed: {
        isPortrait() {
            return this.$mq === 'portrait'
        },
        hasConnection() {
            return this.$store.getters['getConnState']
        },
        showToolbar() {
            return this.viewState === 'ready' && this.cards.length > 0
        },
        showStatusMessage() {
            return ['offline', 'empty', 'error'].includes(this.viewState)
        },
        statusTitle() {
            if (this.viewState === 'empty') {
                return this.$t('allergens.empty.title')
            }
            if (this.viewState === 'error') {
                return this.$t('allergens.error.title')
            }
            return this.$t('allergens.offline.title')
        },
        statusDescription() {
            if (this.viewState === 'empty') {
                return this.$t('allergens.empty.description')
            }
            if (this.viewState === 'error') {
                return this.$t('allergens.error.description')
            }
            return this.$t('allergens.offline.description')
        },
        activeCard() {
            if (!this.cards.length) {
                return null
            }
            return this.cards.find((card) => card.id === this.activeTabId) || this.cards[0]
        }
    },
    watch: {
        hasConnection(isOnline) {
            if (!isOnline) {
                this.viewState = 'offline'
                return
            }
            if (this.viewState === 'offline') {
                this.loadCards()
            }
        }
    },
    mounted() {
        this.loadCards()
    },
    methods: {
        selectCard(cardId) {
            this.activeTabId = cardId == null ? null : String(cardId)
        },
        onSelectChange(event) {
            this.selectCard(event.target.value)
        },
        mapCard(item) {
            const fileUrl = item && item.file_url ? String(item.file_url) : ''
            return {
                id: item && item.id != null ? String(item.id) : '',
                name: item.name || '',
                fileUrl,
                fileName: getFilenameFromUrl(fileUrl) || item.name || ''
            }
        },
        async loadCards() {
            if (!this.hasConnection) {
                this.cards = []
                this.activeTabId = null
                this.viewState = 'offline'
                return
            }

            this.viewState = 'loading'

            try {
                const response = await api.get('allergens')
                if (!response || response.status !== true || !Array.isArray(response.data)) {
                    this.cards = []
                    this.activeTabId = null
                    this.viewState = 'error'
                    return
                }

                const nextCards = response.data.map(this.mapCard).filter((card) => card.id)
                this.cards = nextCards

                if (nextCards.length === 0) {
                    this.activeTabId = null
                    this.viewState = 'empty'
                    return
                }

                const stillSelected = nextCards.some((card) => card.id === this.activeTabId)
                this.activeTabId = stillSelected ? this.activeTabId : nextCards[0].id
                this.viewState = 'ready'
            } catch (error) {
                this.cards = []
                this.activeTabId = null
                this.viewState = 'error'
            }
        },
        async onDownload() {
            if (!this.activeCard || this.isDownloading) {
                return
            }

            if (!this.hasConnection) {
                this.viewState = 'offline'
                return
            }

            this.isDownloading = true

            try {
                const response = await api.download(
                    'allergens/' + this.activeCard.id + '/download?action=download&platform=' + api.getPlatform()
                )
                if (!response || !response.status || !response.blob) {
                    this.$snackbar.error({
                        duration: 5000,
                        closeable: true,
                        message: this.$t('allergens.download_error')
                    })
                    return
                }

                const filename = response.filename || this.activeCard.fileName || this.activeCard.name + '.pdf'
                triggerBlobDownload(response.blob, filename)
            } catch (error) {
                this.$snackbar.error({
                    duration: 5000,
                    closeable: true,
                    message: this.$t('allergens.download_error')
                })
            } finally {
                this.isDownloading = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.allergens {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    padding: 16px 24px 24px;
    box-sizing: border-box;
    background: $neutro-t80;

    .toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 16px;
        flex-shrink: 0;
    }

    .allergens-tabs {
        display: flex;
        align-items: center;
        gap: 8px;
        width: fit-content;
        max-width: 100%;
        padding: 8px;
        box-sizing: border-box;
        border-radius: 4px;
        border: 1px solid #c4ccd9;
        background: #fff;
        overflow-x: auto;
    }

    .allergens-tab {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px;
        border: none;
        border-radius: 4px;
        background-color: transparent;
        color: #727477;
        text-align: center;
        font-feature-settings: 'liga' off, 'clig' off;
        font-family: $text;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        cursor: pointer;
        white-space: nowrap;

        &.selected {
            background-color: #edeff5;
            color: $main;
            font-family: $text-bold;
            font-weight: 700;
        }
    }

    .allergens-select-wrap {
        flex: 1;
        min-width: 0;
    }

    .allergens-select {
        width: 100%;
        min-height: 40px;
        padding: 8px 36px 8px 12px;
        box-sizing: border-box;
        border-radius: 4px;
        border: 1px solid $main;
        background-color: #edeff5;
        background-image: img('down_caret_aaaaaa.svg');
        background-repeat: no-repeat;
        background-position: right 12px center;
        background-size: 10px;
        color: $main;
        font-family: $text;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        cursor: pointer;
    }

    .download-btn {
        display: flex;
        min-height: 36px;
        padding: 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
        border: none;
        border-radius: 4px;
        background: $main-text;
        cursor: pointer;
        transition: opacity 0.2s ease;

        &:hover:not(:disabled) {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: default;
        }

        .i-andy-download {
            display: flex;
            width: 20px;
            height: 20px;
            justify-content: center;
            align-items: center;
            aspect-ratio: 1 / 1;
            color: #fff;
            font-size: 20px;
            line-height: 1;
        }

        span {
            color: #fff;
            font-family: $text-bold;
            font-size: 16px;
            font-style: normal;
            font-weight: 300;
            line-height: 18px;
            margin-top: 2px;
            letter-spacing: 0.5px;
        }
    }

    .allergens-content {
        display: flex;
        flex: 1;
        min-height: 0;
        position: relative;
        overflow: visible;
    }

    &.portrait {
        padding: 12px;

        .toolbar {
            gap: 8px;
        }

        .download-btn {
            min-height: 40px;
        }
    }
}
</style>
